import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import http from '../../services/http';
import { mensagemDoErro, statusDoErro, tomLegivel } from '../../utils/numeroDeWhatsapp';

/**
 * Os templates de mensagem do WhatsApp.
 *
 * ## O que esta tela mostra é o que a Meta diz, agora
 *
 * Não há cópia nossa dos templates. Template aprovado é objeto da Meta e é
 * ela que decide se pode ser usado — uma cópia local diria "aprovado" depois
 * de ela pausar o template por qualidade, e o operador escolheria um template
 * que falha no envio. Por isso cada abertura desta tela consulta a Meta.
 *
 * ## Agrupado por CONTA, não por número
 *
 * Template pertence à conta (WABA). Dois números da mesma conta veem os
 * mesmos templates, então listar por número mostraria tudo duplicado. O
 * cabeçalho de cada grupo traz os rótulos dos números que usam aquela conta,
 * que é o vocabulário de quem abre a tela ("os templates do MAX").
 */

const CATEGORIAS = [
  { valor: 'UTILITY', rotulo: 'Utilidade — aviso sobre algo que o cliente pediu' },
  { valor: 'MARKETING', rotulo: 'Marketing — promoção, novidade, convite' },
  { valor: 'AUTHENTICATION', rotulo: 'Autenticação — código de verificação' },
];

const IDIOMAS = [
  { valor: 'pt_BR', rotulo: 'Português (Brasil)' },
  { valor: 'en_US', rotulo: 'Inglês (EUA)' },
  { valor: 'es', rotulo: 'Espanhol' },
];

/**
 * O status do template, no vocabulário de quem lê.
 *
 * As chaves são o que a Meta manda. `PENDING` virar "Em análise" (e não
 * "Pendente") importa: diz que a espera é DELA, não uma tarefa nossa
 * esquecida.
 */
export const STATUS = {
  APPROVED: { rotulo: 'Aprovado', familia: 'success' },
  PENDING: { rotulo: 'Em análise na Meta', familia: 'warning' },
  IN_APPEAL: { rotulo: 'Em recurso', familia: 'warning' },
  PENDING_DELETION: { rotulo: 'Exclusão em andamento', familia: 'warning' },
  PAUSED: { rotulo: 'Pausado pela Meta', familia: 'warning' },
  REJECTED: { rotulo: 'Rejeitado', familia: 'error' },
  DISABLED: { rotulo: 'Desativado', familia: 'error' },
  DRAFT: { rotulo: 'Rascunho', familia: 'default' },
};

export const statusLegivel = (bruto) => STATUS[String(bruto || '').toUpperCase()]
  || { rotulo: String(bruto || 'Desconhecido'), familia: 'default' };

/** O texto do corpo, de dentro dos `components` que a Meta devolve. */
export const corpoDoTemplate = (template) => {
  const componentes = Array.isArray(template?.components) ? template.components : [];
  const corpo = componentes.find((c) => String(c?.type).toUpperCase() === 'BODY');

  return corpo?.text || '';
};

/**
 * Os exemplos de variável que já estão na Meta.
 *
 * `example.body_text` é lista de LISTAS (o formato dela prevê vários
 * conjuntos). Pegamos o primeiro conjunto, que é o único que mandamos.
 */
export const exemplosDoTemplate = (template) => {
  const componentes = Array.isArray(template?.components) ? template.components : [];
  const corpo = componentes.find((c) => String(c?.type).toUpperCase() === 'BODY');
  const conjuntos = corpo?.example?.body_text;

  if (!Array.isArray(conjuntos) || !Array.isArray(conjuntos[0])) return [];

  return conjuntos[0].map((valor) => String(valor ?? ''));
};

export const rodapeDoTemplate = (template) => {
  const componentes = Array.isArray(template?.components) ? template.components : [];
  const rodape = componentes.find((c) => String(c?.type).toUpperCase() === 'FOOTER');

  return rodape?.text || '';
};

/**
 * As variáveis `{{n}}` de um texto, em ordem numérica.
 *
 * Em ORDEM DO NÚMERO e não de aparição: `Chamado {{2}} de {{1}}` é válido para
 * a Meta, e os exemplos vão na ordem do número. Pela ordem de aparição, o
 * nome iria para o lugar do chamado na análise dela.
 *
 * E ÚNICAS: `{{1}}` duas vezes é uma variável só. Pedir dois exemplos faria a
 * numeração parecer com furo e recusaria um texto perfeitamente válido.
 */
export const variaveisDoTexto = (texto) => {
  const achadas = String(texto || '').match(/\{\{\s*\d+\s*\}\}/g) || [];
  const numeros = achadas.map((m) => parseInt(m.replace(/\D/g, ''), 10));

  return [...new Set(numeros)].sort((a, b) => a - b);
};

/**
 * A numeração está sequencial desde 1?
 *
 * A Meta exige isso e recusa o template com furo (`{{1}}` e `{{3}}`). Avisar
 * aqui evita a pessoa preencher o formulário inteiro para ouvir a recusa.
 */
export const numeracaoValida = (texto) => {
  const vars = variaveisDoTexto(texto);

  return vars.every((numero, i) => numero === i + 1);
};

/**
 * A proporção entre texto e variáveis, que a Meta exige.
 *
 * Ela recusa com *"Esse modelo contém muitas variáveis para sua extensão"*
 * quando o corpo é curto demais para a quantidade de variáveis. A regra
 * publicada é `palavras + variáveis ≥ (3 × variáveis) + 1`.
 *
 * ## Por que exigimos FOLGA, e não o empate
 *
 * Porque o empate foi recusado na prática. `Olá {{1}}, seu chamado {{2}} foi
 * atualizado.` dá 5 palavras + 2 variáveis = 7, contra 7 exigidos pela
 * fórmula — e a Meta recusou. Ou seja, a contagem dela não é exatamente esta.
 * Avisar no empate é o lado seguro do erro.
 *
 * ## E por que isto AVISA em vez de bloquear
 *
 * As outras duas regras desta tela (numeração e exemplo faltando) são exatas
 * e documentadas, e por isso o servidor recusa. Esta é uma APROXIMAÇÃO da
 * contagem dela. Bloquear por aproximação impediria um template que ela
 * aceitaria, e a pessoa não teria como seguir — o erro caro na direção
 * oposta. Quem decide continua sendo a Meta; isto só evita a ida e volta.
 */
export const proporcaoDasVariaveis = (texto) => {
  const variaveis = variaveisDoTexto(texto).length;

  // As palavras do texto SEM as variáveis: `{{1}}` não é palavra, é o
  // parâmetro — ele entra na conta pelo seu próprio lado da fórmula.
  const palavras = String(texto || '')
    .replace(/\{\{\s*\d+\s*\}\}/g, ' ')
    .split(/\s+/)
    .filter((p) => p.replace(/[^\p{L}\p{N}]/gu, '') !== '').length;

  const minimo = (3 * variaveis) + 1;

  return {
    palavras,
    variaveis,
    minimo,
    total: palavras + variaveis,
    // Sem variável nenhuma não há proporção a respeitar.
    folgada: variaveis === 0 || (palavras + variaveis) > minimo,
  };
};

/**
 * Os exemplos recortados no tamanho das variáveis que o texto TEM agora.
 *
 * Quem apaga uma variável do texto deixa o exemplo dela para trás no estado
 * do formulário. Mandar a lista crua faria a contagem não bater com as
 * variáveis e a Meta recusaria — por causa de um campo que a pessoa não vê
 * mais na tela.
 */
export const exemplosNaOrdem = (form) => variaveisDoTexto(form?.corpo)
  .map((numero, indice) => String(form?.exemplos?.[indice] ?? ''));

const formularioVazio = {
  nome: '',
  categoria: 'UTILITY',
  idioma: 'pt_BR',
  corpo: '',
  rodape: '',
  exemplos: [],
};

const TemplatesDoWhatsApp = () => {
  // Quais CONTAS estão abertas, por `waba_id`. Mapa, e não uma só: abrir uma
  // não fecha a outra -- comparar os templates de duas contas é justamente o
  // que se faz aqui.
  //
  // Todas começam FECHADAS. São três contas em produção; abrir a aba com as
  // nove mensagens inteiras era o problema que isto resolve.
  const [contasAbertas, setContasAbertas] = useState({});

  const alternarConta = useCallback((wabaId) => {
    setContasAbertas((atual) => ({ ...atual, [wabaId]: !atual[wabaId] }));
  }, []);

  const [contas, setContas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [semPermissao, setSemPermissao] = useState(false);

  const [form, setForm] = useState(formularioVazio);
  const [dialogo, setDialogo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [paraExcluir, setParaExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    setSemPermissao(false);
    try {
      const resposta = await http.get('/whatsapp-templates');
      setContas(Array.isArray(resposta?.data?.data?.contas) ? resposta.data.data.contas : []);
    } catch (e) {
      if (statusDoErro(e) === 403) {
        setSemPermissao(true);
      } else {
        setErro(mensagemDoErro(e, 'Não foi possível carregar os templates.'));
      }
      setContas([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const mudar = (campo) => (evento) => setForm((atual) => ({ ...atual, [campo]: evento.target.value }));

  const abrirCriacao = (conta) => {
    setForm(formularioVazio);
    setDialogo({ modo: 'criar', conta });
  };

  const abrirEdicao = (conta, template) => {
    setForm({
      nome: template.name,
      categoria: template.category || 'UTILITY',
      idioma: template.language || 'pt_BR',
      corpo: corpoDoTemplate(template),
      rodape: rodapeDoTemplate(template),
      // Os exemplos que já estão na Meta, para não serem redigitados. Eles
      // vêm em `example.body_text` do próprio componente BODY.
      exemplos: exemplosDoTemplate(template),
    });
    setDialogo({ modo: 'editar', conta, template });
  };

  const mudarExemplo = (indice) => (evento) => setForm((atual) => {
    const exemplos = [...(atual.exemplos || [])];
    exemplos[indice] = evento.target.value;

    return { ...atual, exemplos };
  });

  const salvar = async () => {
    if (!dialogo) return;

    setSalvando(true);
    try {
      if (dialogo.modo === 'criar') {
        const resposta = await http.post('/whatsapp-templates', {
          whatsapp_number_id: dialogo.conta.whatsapp_number_id,
          nome: form.nome,
          categoria: form.categoria,
          idioma: form.idioma,
          corpo: form.corpo,
          rodape: form.rodape,
          exemplos: exemplosNaOrdem(form),
        });

        // O status vem da Meta e quase sempre é "em análise". Dizer só
        // "criado" faria o operador tentar usar um template que ainda não
        // pode ser enviado.
        const status = statusLegivel(resposta?.data?.data?.status).rotulo;
        toast.success(`Template criado. Situação na Meta: ${status.toLowerCase()}.`);
      } else {
        await http.put(`/whatsapp-templates/${dialogo.template.id}`, {
          whatsapp_number_id: dialogo.conta.whatsapp_number_id,
          corpo: form.corpo,
          rodape: form.rodape,
          // Também na edição: mudar o texto pode acrescentar ou tirar
          // variáveis, e a Meta exige o exemplo de cada uma que ficar.
          exemplos: exemplosNaOrdem(form),
        });
        toast.success('Template enviado para análise com as alterações.');
      }

      setDialogo(null);
      await carregar();
    } catch (e) {
      // A mensagem da Meta é específica e é ela que conserta ("component BODY
      // exceeds limit", "template name already exists"). Trocar por um texto
      // genérico perderia justamente a parte útil.
      toast.error(mensagemDoErro(e, 'Não foi possível salvar o template.'));
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    if (!paraExcluir) return;

    setExcluindo(true);
    try {
      await http.delete('/whatsapp-templates', {
        data: {
          whatsapp_number_id: paraExcluir.conta.whatsapp_number_id,
          nome: paraExcluir.template.name,
        },
      });
      toast.success('Template excluído.');
      setParaExcluir(null);
      await carregar();
    } catch (e) {
      toast.error(mensagemDoErro(e, 'Não foi possível excluir o template.'));
    } finally {
      setExcluindo(false);
    }
  };

  if (semPermissao) {
    // A página inteira já mostra o bloco de "acesso não liberado".
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="templates-do-whatsapp">
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1,
      }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Templates de mensagem
          </Typography>
          <Typography variant="caption" color="text.secondary">
            São da Meta, não do AloDesk: ela aprova cada um antes de poder ser usado. Servem para
            escrever primeiro ou fora das 24 horas desde a última mensagem do cliente.
          </Typography>
        </Box>
        <Tooltip title="Atualizar">
          <IconButton size="small" onClick={carregar} aria-label="Atualizar templates">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {carregando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!carregando && erro && <Alert severity="error" sx={{ mt: 1 }}>{erro}</Alert>}

      {!carregando && !erro && contas.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          Nenhuma conta de WhatsApp cadastrada.
        </Typography>
      )}

      {!carregando && !erro && contas.map((conta) => (
        <Box
          key={conta.waba_id}
          data-testid={`conta-${conta.waba_id}`}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, mt: 1.5 }}
        >
          {/*
            A CONTA colapsa, e não cada template. Com três contas e nove
            templates, abrir a aba mostrava as nove mensagens inteiras de uma
            vez -- 1756px, medidos em produção. Fechadas, são três linhas.

            O cabeçalho é clicável inteiro, não só a seta: alvo de 24px no
            canto é mira, e aqui a linha toda não faz outra coisa.
          */}
          <Box
            role="button"
            tabIndex={0}
            onClick={() => alternarConta(conta.waba_id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                alternarConta(conta.waba_id);
              }
            }}
            aria-expanded={Boolean(contasAbertas[conta.waba_id])}
            aria-label={`${contasAbertas[conta.waba_id] ? 'Recolher' : 'Expandir'} ${conta.rotulos.join(', ')}`}
            data-testid={`alternar-conta-${conta.waba_id}`}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 1, flexWrap: 'wrap', cursor: 'pointer', borderRadius: 1,
              '&:hover': { backgroundColor: 'action.hover' },
              '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              {contasAbertas[conta.waba_id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {conta.rotulos.join(', ')}
              </Typography>
              {/* A contagem fica no cabeçalho porque é a informação que
                  decide se vale abrir. Sem ela, fechado não diz nada. */}
              {conta.ok && (
                <Typography variant="caption" color="text.secondary">
                  {conta.templates.length === 1 ? '1 template' : `${conta.templates.length} templates`}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              // O clique não pode subir para o cabeçalho: abrir o formulário
              // E recolher a conta no mesmo gesto esconderia o que acabou de
              // ser criado.
              onClick={(e) => { e.stopPropagation(); abrirCriacao(conta); }}
              disabled={!conta.ok}
            >
              Adicionar template
            </Button>
          </Box>

          {/* O erro é POR CONTA, e não da tela: o problema costuma ser de uma
              só (token sem permissão de gerenciar templates, por exemplo), e
              derrubar tudo esconderia os templates que estão disponíveis.

              Fica FORA do colapso, sempre visível: um aviso escondido atrás
              de um clique não avisa ninguém. */}
          {!conta.ok && <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>{conta.erro}</Alert>}

          {contasAbertas[conta.waba_id] && (
          <Box sx={{ mt: 1 }}>

          {conta.ok && conta.templates.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              Esta conta ainda não tem nenhum template.
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {conta.templates.map((template) => {
              const situacao = statusLegivel(template.status);

              return (
                <Box
                  key={`${template.id}-${template.language}`}
                  data-testid={`template-${template.id}`}
                  sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}
                >
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                        {template.name}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={situacao.rotulo}
                        data-testid={`status-${template.id}`}
                        sx={{
                          color: tomLegivel(situacao.familia),
                          borderColor: tomLegivel(situacao.familia),
                        }}
                      />
                      <Chip size="small" variant="outlined" label={template.language} />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => abrirEdicao(conta, template)}
                          aria-label={`Editar ${template.name}`}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => setParaExcluir({ conta, template })}
                          aria-label={`Excluir ${template.name}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Dentro da conta aberta a mensagem aparece INTEIRA: quem
                      abriu a conta abriu para ler. O colapso está um nível
                      acima, na conta. */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid={`corpo-${template.id}`}
                    sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}
                  >
                    {corpoDoTemplate(template)}
                  </Typography>

                  {/* O MOTIVO da rejeição, e não só "rejeitado". Sem ele a
                      pessoa não tem como consertar e reenvia igual. */}
                  {template.rejected_reason
                    && String(template.rejected_reason).toUpperCase() !== 'NONE' && (
                    <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                      Motivo da rejeição: {template.rejected_reason}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
          </Box>
          )}
        </Box>
      ))}

      {/* ---------------------------------------------- criar / editar */}

      <Dialog open={Boolean(dialogo)} onClose={() => setDialogo(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialogo?.modo === 'editar' ? `Editar ${dialogo.template.name}` : 'Novo template'}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            A Meta analisa cada template antes de liberar o uso. Isso vale também para alterações:
            editar um template aprovado o devolve para análise.
          </Alert>

          <TextField
            label="Nome"
            fullWidth
            value={form.nome}
            onChange={mudar('nome')}
            // Na edição o nome não muda: a Meta não permite, e um campo
            // editável que a API ignora é pior que um campo travado.
            disabled={dialogo?.modo === 'editar'}
            helperText="Só letras minúsculas, números e sublinhado — sem espaço e sem acento. É regra da Meta."
            inputProps={{ 'data-testid': 'nome-do-template' }}
            sx={{ mb: 2 }}
          />

          <TextField
            select
            label="Categoria"
            fullWidth
            value={form.categoria}
            onChange={mudar('categoria')}
            helperText="A Meta cobra preços diferentes por categoria, e recusa o template se a categoria não combinar com o texto."
            sx={{ mb: 2 }}
          >
            {CATEGORIAS.map((c) => (
              <MenuItem key={c.valor} value={c.valor}>{c.rotulo}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Idioma"
            fullWidth
            value={form.idioma}
            onChange={mudar('idioma')}
            disabled={dialogo?.modo === 'editar'}
            sx={{ mb: 2 }}
          >
            {IDIOMAS.map((i) => (
              <MenuItem key={i.valor} value={i.valor}>{i.rotulo}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Mensagem"
            fullWidth
            multiline
            minRows={4}
            value={form.corpo}
            onChange={mudar('corpo')}
            helperText={'Use {{1}}, {{2}}... onde o texto muda a cada envio. Até 1024 caracteres.'}
            inputProps={{ 'data-testid': 'corpo-do-template' }}
            error={!numeracaoValida(form.corpo)}
            sx={{ mb: 2 }}
          />

          {/* Os EXEMPLOS, um por variável.
              A Meta exige um exemplo de cada `{{n}}` para analisar o template
              -- sem eles ela recusa a criação. Os campos aparecem conforme a
              pessoa escreve as variáveis, em vez de existirem sempre: um
              template sem variável não tem o que exemplificar. */}
          {!numeracaoValida(form.corpo) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              As variáveis têm de ser numeradas em sequência a partir de
              {' '}
              {'{{1}}'}
              , sem pular número. É regra da Meta.
            </Alert>
          )}

          {/* A PROPORÇÃO. Avisa, não bloqueia — ver `proporcaoDasVariaveis`.
              O botão de salvar continua ativo de propósito: quem decide é a
              Meta, e uma aproximação nossa não pode impedir um template que
              ela aceitaria. */}
          {numeracaoValida(form.corpo) && !proporcaoDasVariaveis(form.corpo).folgada && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="aviso-de-proporcao">
              A Meta costuma recusar mensagens curtas com muitas variáveis. Esta tem
              {' '}{proporcaoDasVariaveis(form.corpo).palavras} palavra(s) e
              {' '}{proporcaoDasVariaveis(form.corpo).variaveis} variável(is) — escreva uma frase
              mais completa em volta delas, ou use menos variáveis. Dá para tentar assim mesmo:
              quem decide é ela.
            </Alert>
          )}

          {numeracaoValida(form.corpo) && variaveisDoTexto(form.corpo).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                A Meta precisa de um exemplo de cada variável para analisar o template. Os exemplos
                não são enviados ao cliente — servem só para ela entender o texto.
              </Typography>

              {variaveisDoTexto(form.corpo).map((numero, indice) => (
                <TextField
                  key={numero}
                  label={`Exemplo para {{${numero}}}`}
                  fullWidth
                  size="small"
                  value={form.exemplos?.[indice] || ''}
                  onChange={mudarExemplo(indice)}
                  inputProps={{ 'data-testid': `exemplo-${numero}` }}
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
          )}

          <TextField
            label="Rodapé (opcional)"
            fullWidth
            value={form.rodape}
            onChange={mudar('rodape')}
            helperText="Linha pequena no fim da mensagem. Até 60 caracteres."
            inputProps={{ 'data-testid': 'rodape-do-template' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogo(null)} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>
            {dialogo?.modo === 'editar' ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------ excluir */}

      <Dialog open={Boolean(paraExcluir)} onClose={() => setParaExcluir(null)} fullWidth maxWidth="xs">
        <DialogTitle>Excluir template</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Excluir <strong>{paraExcluir?.template?.name}</strong>?
          </Typography>
          {/* O efeito que ninguém adivinha: a Meta exclui PELO NOME, e o nome
              é o mesmo em todos os idiomas. Sem este aviso alguém apaga o
              espanhol querendo apagar o português. */}
          <Alert severity="warning" sx={{ mt: 2 }}>
            A Meta exclui o template em TODOS os idiomas em que ele existe — não dá para apagar só
            uma tradução. A exclusão não pode ser desfeita.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParaExcluir(null)} disabled={excluindo}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={excluir} disabled={excluindo}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TemplatesDoWhatsApp;
